import { useEffect } from 'react'
import { X } from 'lucide-react'

import { Button } from '@/components/ui/button'

import { CaseDetailBody } from './CaseDetailBody'

/**
 * Slide-over drawer surfacing the case detail without leaving the queue.
 *
 * <p>Why: the queue is the agent's anchor. Pushing them to a full-page
 * /cases/{id} loses the bucket filter, scroll position, and search state.
 * A drawer keeps that anchor while still letting the manager work the case
 * end-to-end. Deep-linkable via /cases/{id} for shared URLs.</p>
 *
 * <p>Controlled — caller owns the open/close state via {@code caseId}
 * (string when open, null when closed). Esc + backdrop click both close.</p>
 */
export function CaseDetailDrawer({
  caseId,
  onClose,
}: {
  caseId: string | null
  onClose: () => void
}) {
  // Esc closes. Doing this with a window listener (not useImperativeHandle
  // or focus traps) keeps the surface tiny and the component portable.
  useEffect(() => {
    if (!caseId) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [caseId, onClose])

  // Lock body scroll while open — agents on touch devices accidentally scroll
  // the underlying queue otherwise.
  useEffect(() => {
    if (!caseId) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [caseId])

  if (!caseId) return null

  // Starts below the app's sticky header (h-13) rather than at the top of
  // the viewport: covering it hid the tenant, env pill and user menu. One
  // layer under the header (z-20) so the user menu still drops over it.
  return (
    <div
      className="fixed inset-x-0 bottom-0 top-13 z-[19] flex"
      role="dialog"
      aria-modal="true"
      aria-label="Case detail"
    >
      {/* Backdrop: dim everything underneath. Click anywhere off-panel to
          close. Solid 65% navy + 4px blur masks the queue almost completely
          so the slide-in doesn't read as a flash of the underlying page. */}
      <button
        type="button"
        aria-label="Close case detail"
        onClick={onClose}
        className="flex-1 bg-navy-900/65 backdrop-blur"
      />
      {/* Drawer panel — solid surface so the queue underneath never bleeds
          through. Slide-in is short (130ms) so the panel feels instant
          rather than animated-in. */}
      <aside
        className="flex h-full w-full max-w-[min(1100px,95vw)] flex-col bg-navy-50 shadow-lift animate-slide-in-right"
        style={{ animationDuration: '130ms' }}
      >
        {/* Header bar */}
        <div className="flex h-13 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-5">
          <span className="text-2xs font-semibold uppercase tracking-wider text-navy-700">
            Case detail
          </span>
          <Button variant="ghost" size="sm" onClick={onClose} aria-label="Close">
            <X className="h-3.5 w-3.5" />
            Close
          </Button>
        </div>
        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 lg:p-6">
          <CaseDetailBody caseId={caseId} embedded />
        </div>
      </aside>
    </div>
  )
}
